import { accountAccessShowLogin } from 'actions/UiActions'
import { BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE, BUSINESS_TRAVELLER_ROOT_ROUTE } from 'components/App/Routes/routeConstants'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import LeBusinessTravellerLogo from 'components/App/Header/Logo/logos/LeBusinessTravellerLogo'
import Clickable from 'components/Common/Clickable/Clickable'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import { InverseOutlineTextButton } from 'LuxKit/Button/OutlineTextButton'
import TextLink from 'LuxKit/TextLink'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { breakpointSizes, mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import BusinessTravellerRequestDemoModal from 'businessTraveller/components/demo-form/BusinessTravellerRequestDemoModal'
import { getImageUrl } from 'lib/image/imageUtils'
import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

const ADMIN_ROW_BG = '#252830'
const HERO_BG = '#363A45'
const HERO_BANNER_GRADIENT = 'radial-gradient(371.9% 111.8% at 50% 0%, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.05) 100%), linear-gradient(140deg, rgba(255, 255, 255, 0.05) 7.04%, rgba(255, 255, 255, 0.00) 49.89%)'

// Admin banner - solid background, no gradient
const AdminBanner = styled.div`
  position: relative;
  z-index: 1;
  background-color: ${ADMIN_ROW_BG};
`

const BannerContentGroup = styled(Group)`
  height: ${rem(40)};
`

// Wrapper for header row + hero with gradients
const GradientWrapper = styled.div`
  position: relative;
  background-color: ${HERO_BG};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${HERO_BANNER_GRADIENT};
    pointer-events: none;
  }
`

const HeaderContentGroup = styled(Group)`
  height: ${rem(64)};

  ${mediaQueryUp.tablet} {
    height: ${rem(74)};
  }
`

const SmallScreenLogo = styled.span`
  display: inline-flex;

  @media (min-width: 450px) {
    display: none;
  }
`

const WideScreenLogo = styled.span`
  display: none;

  @media (min-width: 450px) {
    display: inline-flex;
  }
`

const HeroContainer = styled(LayoutContainer)`
  position: relative;
  display: flex;
  flex-direction: column;
  padding-top: ${rem(20)};
  padding-bottom: ${rem(28)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(24)};
    padding-bottom: ${rem(36)};
  }
`

const ContentArea = styled.div`
  flex: 1;
  order: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(28)};
  padding-top: ${rem(28)};

  ${mediaQueryUp.tablet} {
    order: 0;
    padding-top: ${rem(40)};
    padding-bottom: ${rem(40)};
  }
`

const ImageArea = styled.div`
  order: 0;

  ${mediaQueryUp.tablet} {
    order: 1;
    flex: 0 0 50%;
    display: flex;
    align-items: center;
    margin-bottom: 0;
  }
`

const HeroImage = styled.img`
  width: 100%;
  border-radius: ${rem(8)};
  object-fit: cover;
  aspect-ratio: 279 / 160;

  ${mediaQueryUp.tablet} {
    aspect-ratio: 558 / 408;
  }
`

const HeadingWrapper = styled.div`
  ${mediaQueryUp.tablet} {
    max-width: ${rem(550)};
  }
`

const SubtitleWrapper = styled.div`
  ${mediaQueryUp.tablet} {
    max-width: ${rem(470)};
  }
`

function SalesLandingHeroWithHeader() {
  const appDispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  const openLogin = useCallback(() => {
    appDispatch(accountAccessShowLogin())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openDemoModal = useCallback(() => {
    showModal(<BusinessTravellerRequestDemoModal />)
  }, [showModal])

  return (
    <>
      {/* Admin Banner - solid background, no gradient */}
      <AdminBanner>
        <Pane paletteType="inverse" background="none">
          <LayoutContainer>
            <BannerContentGroup
              direction="horizontal"
              verticalAlign="center"
              horizontalAlign="end"
            >
              <TextButton
                kind="tertiary"
                href={config.businessTraveller.businessAdminPortalHref}
                target="_blank"
                horizontalOutdent="end"
              >
                Business Traveller Admin
              </TextButton>
            </BannerContentGroup>
          </LayoutContainer>
        </Pane>
      </AdminBanner>

      {/* Header row + Hero with gradients */}
      <GradientWrapper>
        <Pane paletteType="inverse" background="none">
          {/* Header Row */}
          <LayoutContainer>
            <HeaderContentGroup
              direction="horizontal"
              horizontalAlign="space-between"
              gap={8}
              verticalAlign="center"
            >
              <Clickable to={BUSINESS_TRAVELLER_ROOT_ROUTE}>
                <SmallScreenLogo><LogoIcon fill="white" height={35} /></SmallScreenLogo>
                <WideScreenLogo><LeBusinessTravellerLogo fill="white" height={35} width={280} /></WideScreenLogo>
              </Clickable>

              <Group direction="horizontal" gap={8}>
                <CSSBreakpoint max="mobile">
                  <IconButton kind="tertiary" onClick={openLogin}>
                    <LineUserIcon />
                  </IconButton>
                </CSSBreakpoint>
                <CSSBreakpoint min="tablet">
                  <TextButton kind="tertiary" onClick={openLogin}>
                    Sign in
                  </TextButton>
                </CSSBreakpoint>
                <TextButton kind="primary" to={BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE}>
                  Sign up
                </TextButton>
              </Group>
            </HeaderContentGroup>
          </LayoutContainer>

          {/* Hero Content */}
          <HeroContainer>
            <ContentArea>
              <Group direction="vertical" gap={16}>
                <HeadingWrapper>
                  <Heading variant="heading1">Smarter business travel <i>starts here</i></Heading>
                </HeadingWrapper>
                <SubtitleWrapper>
                  <BodyText variant="large">
                    Spend less on corporate travel while managing costs and approvals all in one place.
                  </BodyText>
                </SubtitleWrapper>
              </Group>

              <Group direction="vertical" gap={12} tabletDirection="horizontal">
                <TextButton kind="primary" fit="mobile-full-width" size="medium" to={BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE}>
                  Get started for free
                </TextButton>
                <InverseOutlineTextButton kind="secondary" fit="mobile-full-width" size="medium" onClick={openDemoModal}>
                  Book a free demo
                </InverseOutlineTextButton>
              </Group>

              <Group direction="horizontal" gap={12} verticalAlign="center">
                <BodyText variant="medium">Already registered?</BodyText>
                <TextLink size="medium" onClick={openLogin}>Sign in</TextLink>
              </Group>
            </ContentArea>

            <ImageArea>
              <picture>
                <source media={`(min-width: ${breakpointSizes.tablet.min}px)`} srcSet={getImageUrl('lebt-landing-hero-img-v3')} />
                <HeroImage src={getImageUrl('lebt-landing-hero-img-small')} alt="Business traveller" />
              </picture>
            </ImageArea>
          </HeroContainer>
        </Pane>
      </GradientWrapper>
    </>
  )
}

export default SalesLandingHeroWithHeader
